<template>
    <div>
        <div v-if="fileUrl">
            <img ref="cropperImage" :src="fileUrl" alt=""/>
        </div>

        <label v-if="label" class="block text-sm font-medium leading-6 text-gray-900" v-text="label"/>

        <fieldset v-if="cropper" class="mt-4">
            <legend class="text-sm font-semibold text-gray-900">
                {{ trans('homepage.crop-format') }}
            </legend>

            <div class="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-5">
                <button
                    v-for="option in aspectRatioOptions"
                    :key="option.key"
                    type="button"
                    class="rounded-lg border px-3 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    :class="selectedAspectRatioKey === option.key
                        ? 'border-green-500 bg-green-50 text-green-800'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'"
                    :aria-pressed="selectedAspectRatioKey === option.key"
                    @click="setAspectRatio(option.key)"
                >
                    {{ option.label }}
                </button>
            </div>
        </fieldset>

        <div v-if="cropper" class="flex mt-4 justify-between">
            <QrButton @click="reset" :text="trans('button.cancel')" trans="button.cancel"/>
        </div>
        <div
            v-else
            class="mt-2 flex justify-center rounded-lg border border-dashed px-6 py-10"
            :class="active ? 'border-gray-900/50' : 'border-gray-900/25'"
            @dragenter.prevent="setActive"
            @dragover.prevent="setActive"
            @dragleave.prevent="setInactive"
            @drop.prevent="onDrop"
        >
            <div>
                <div v-if="! file" class="text-center">
                    <QrIcon class="hidden md:block mx-auto h-12 w-12 text-gray-300" icon="image"/>
                    <div class="mt-4 flex justify-center text-sm leading-6 text-gray-600">
                        <label
                            for="file-upload"
                            class="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                        >
                            <span v-text="trans('form.select-a-file')"/>
                            <input
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                class="sr-only"
                                @change="onInputChange"
                                :multiple="false"
                            />
                        </label>
                        <p class="pl-1" v-text="trans('form.or-drag-and-drop')"/>
                    </div>
                    <p class="text-xs leading-5 text-gray-600" v-text="helperText"/>
                </div>

            </div>
        </div>
    </div>
</template>

<script setup>
import Cropper from 'cropperjs';
import QrIcon from "@/components/application-ui/elements/QrIcon.vue";
import { trans } from "@/libs/helper.js";
import { computed, onMounted, onUnmounted, ref, nextTick } from 'vue';
import useFileList from "@/libs/file-list.js";
import QrButton from "@/components/application-ui/elements/QrButton.vue";
import createUploader from "@/libs/file-uploader.js";

const fileUrl = ref(null);
const cropperImage = ref(null);
const cropper = ref(null);
const selectedAspectRatioKey = ref('16:9');
const aspectRatioOptions = [
    { key: '16:9', label: '16:9', value: 16 / 9 },
    { key: '4:3', label: '4:3', value: 4 / 3 },
    { key: '1:1', label: '1:1', value: 1 },
    { key: '3:4', label: '3:4', value: 3 / 4 },
    { key: '9:16', label: '9:16', value: 9 / 16 },
];
const emit = defineEmits(['files-dropped', 'upload-complete']);
const props = defineProps({
    label: String,
    helperText: {
        required: true,
        type: String
    },
    errors: {
        required: false,
        type: Array
    },
    endpoint: String,
    cropImage: Boolean,
});

const preventDefaults = (e) => {
    e.preventDefault();
}

onMounted(() => {
    events.forEach((eventName) => {
        document.body.addEventListener(eventName, preventDefaults)
    });
});

onUnmounted(() => {
    events.forEach((eventName) => {
        document.body.removeEventListener(eventName, preventDefaults)
    });
});

const { file, addFiles, removeFiles } = useFileList();
const events = ['dragenter', 'dragover', 'dragleave', 'drop'];
const active = ref(false);
let inActiveTimeout = null;

const setFilUrlAndInitiateCropper = async () => {
    const reader = new FileReader();
    reader.onload = async () => {
        fileUrl.value = reader.result
        const dimensions = await getImageDimensions(reader.result);
        selectedAspectRatioKey.value = dimensions.height > dimensions.width ? '3:4' : '16:9';

        await nextTick();

        cropper.value = new Cropper(cropperImage.value, {
            viewMode: 2,
            dragMode: 'move',
            aspectRatio: selectedAspectRatio.value,
            restore: false,
            zoomable: true,
            movable: true,
            guides: false,
            center: true,
            highlight: true,
            cropBoxMovable: true,
            cropBoxResizable: true,
            toggleDragModeOnDblclick: false,
        });
    };

    reader.readAsDataURL(file.value.file);
}

const selectedAspectRatio = computed(() => {
    const option = aspectRatioOptions.find(item => item.key === selectedAspectRatioKey.value)
        ?? aspectRatioOptions[0];

    return option.value;
});

const setAspectRatio = (key) => {
    const option = aspectRatioOptions.find(item => item.key === key);

    if (! option || ! cropper.value) {
        return;
    }

    selectedAspectRatioKey.value = key;
    cropper.value.setAspectRatio(option.value);
};

const getImageDimensions = (source) => {
    return new Promise((resolve) => {
        const image = new Image();

        image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
        image.onerror = () => resolve({ width: 16, height: 9 });
        image.src = source;
    });
};

const setActive = () => {
    active.value = true
    clearTimeout(inActiveTimeout)
}

const setInactive = () => {
    inActiveTimeout = setTimeout(() => {
        active.value = false
    }, 50)
}

const onDrop = (e) => {
    active.value = false;
    const filesToAdd = Array.prototype.slice.call(e.dataTransfer.files, 0, props.maxFiles);
    addFiles([...filesToAdd]);
    setFilUrlAndInitiateCropper();
}

const onInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = null;
    setFilUrlAndInitiateCropper();
}

const uploading = computed(() => {
    return file.value?.status === 'loading';
});

const canUpload = computed(() => {
    return Boolean(cropper.value && file.value) && ! uploading.value;
});

const uploadComplete = ({ status, data }) => {
    emit('upload-complete', { status, data });
    reset();
}

const upload = () => {
    const cropData = cropper.value.getData();
    const scale = Math.min(1, 1920 / cropData.width, 1920 / cropData.height);
    const width = Math.max(1, Math.round(cropData.width * scale));
    const height = Math.max(1, Math.round(cropData.height * scale));
    const canvas = cropper.value.getCroppedCanvas({
        width,
        height,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
    });

    canvas.toBlob((blob) => {
        if (! blob) {
            return;
        }

        file.value.file = new File([blob], 'main-image.jpg', { type: 'image/jpeg' });
        uploadFile(file.value);
    }, 'image/jpeg', 0.9);
};

const reset = () => {
    removeFiles();
    cropper.value?.destroy();
    cropper.value = null;
    selectedAspectRatioKey.value = '16:9';
    fileUrl.value = null;
};

const { uploadFile } = createUploader(
    props.endpoint,
    uploadComplete
);

defineExpose({
    canUpload,
    upload,
    uploading,
});

</script>
